import { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, Loader2, Plus, Trash2, ChevronLeft } from 'lucide-react';
import { useChatStore } from '../../stores/chatStore';
import { useChatContext } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { ConversationList } from './ConversationList';
import { MessageBubble } from './MessageBubble';
import { MessageInput } from './MessageInput';
import { NewMessageModal } from './NewMessageModal';
import { Message } from '../../types/chat';
import { useUserProfile } from '../../hooks/useUserProfile';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import { UserAvatar } from '../Common/UserAvatar';

interface ChatModalContentProps {
    variant?: 'modal' | 'page';
}

/**
 * ChatModalContent - Unified chat UI for both Modal and Page views
 * Now uses ChatContext for global state management
 */
export function ChatModalContent({ variant = 'modal' }: ChatModalContentProps) {
    const isModal = variant === 'modal';
    // ===== GLOBAL STATE (from ChatContext) =====
    const {
        conversations,
        sendMessage,
        markAsRead,
        fetchConversation,
        hideConversation,
        lastReceivedMessage
    } = useChatContext();
    const { activeConversation, setActiveConversation, closeChat, typing, presence } = useChatStore();
    const { pubkey } = useAuth();

    // ===== LOCAL STATE =====
    const [messages, setMessages] = useState<Message[]>([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [showNewMessage, setShowNewMessage] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);

    // Auto-scroll ref
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Find active conversation in list
    const activeConv = conversations.find(c => c.pubkey === activeConversation);

    // Always call hook - resolve profile for active conversation if not in list
    const shouldResolveProfile = Boolean(activeConversation && !activeConv);
    const { profile: newUserProfile, loading: profileLoading } = useUserProfile(
        shouldResolveProfile ? activeConversation : null
    );

    // Create display conversation object (memoized for performance)
    const displayConv = useMemo(() => {
        if (activeConv) return activeConv;

        if (shouldResolveProfile && newUserProfile) {
            return {
                pubkey: activeConversation!,
                name: newUserProfile.name,
                avatar: newUserProfile.avatar || null,
                lastMessage: '',
                timestamp: Date.now(),
                unread: 0,
            };
        }

        return null;
    }, [activeConv, shouldResolveProfile, newUserProfile, activeConversation]);

    // ===== AUTO-SCROLL =====
    const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
        messagesEndRef.current?.scrollIntoView({ behavior });
    };

    const initialScrollRef = useRef(false);

    // Reset initial scroll when conversation changes
    useEffect(() => {
        initialScrollRef.current = false;
    }, [activeConversation]);

    // Handle scrolling on messages update
    useEffect(() => {
        if (messages.length > 0) {
            if (!initialScrollRef.current) {
                // First load -> Instant scroll
                scrollToBottom('auto');

                // Safety double-check for layout shifts (images, etc)
                setTimeout(() => scrollToBottom('auto'), 100);

                initialScrollRef.current = true;
            } else {
                // New messages -> Smooth scroll
                scrollToBottom('smooth');
            }
        }
    }, [messages]);

    // ===== LOAD MESSAGES =====
    useEffect(() => {
        if (!activeConversation) {
            setMessages([]);
            return;
        }

        let cancelled = false;

        async function loadMessages() {
            if (!activeConversation) return;

            setLoadingMessages(true);
            try {
                const history = await fetchConversation(activeConversation);

                if (!cancelled) {
                    setMessages(history);
                    markAsRead(activeConversation);
                }
            } catch (error) {
                console.error('Failed to load messages:', error);
            } finally {
                if (!cancelled) {
                    setLoadingMessages(false);
                }
            }
        }

        loadMessages();

        return () => {
            cancelled = true;
        };
    }, [activeConversation, fetchConversation, markAsRead]);

    // ===== REAL-TIME MESSAGE SYNC =====
    useEffect(() => {
        if (!lastReceivedMessage || !activeConversation) return;

        const isFromPartner = lastReceivedMessage.sender === activeConversation;
        const isToPartner = lastReceivedMessage.recipient === activeConversation;

        if (isFromPartner || isToPartner) {
            if (isFromPartner && activeConversation) {
                markAsRead(activeConversation);
            }

            setMessages(prev => {
                // 1. Check if we already have this specific event ID
                if (prev.some(m => m.id === lastReceivedMessage.id)) return prev;

                // 2. If it's a message we sent, look for its optimistic temp version
                if (lastReceivedMessage.sent) {
                    const tempMessage = prev.find(m =>
                        m.id.startsWith('temp-') &&
                        m.content === lastReceivedMessage.content
                    );

                    if (tempMessage) {
                        // Replace temp message with real one
                        return prev.map(m =>
                            m.id === tempMessage.id ? lastReceivedMessage : m
                        );
                    }
                }

                return [...prev, lastReceivedMessage];
            });
        }
    }, [lastReceivedMessage, activeConversation, markAsRead]);

    // ===== SEND MESSAGE WITH OPTIMISTIC UI =====
    const handleSendMessage = async (content: string) => {
        if (!activeConversation || sendingMessage) return;

        // Optimistic message
        const tempMessage: Message = {
            id: `temp-${Date.now()}`,
            sender: pubkey as string,
            recipient: activeConversation,
            content,
            timestamp: Math.floor(Date.now() / 1000),
            sent: true,
            status: 'sending', // 'sending' | 'sent' | 'failed'
        };

        // Add to UI immediately
        setMessages(prev => [...prev, tempMessage]);
        setSendingMessage(true);

        try {
            await sendMessage(activeConversation, content);

            // Mark as sent
            setMessages(prev =>
                prev.map(m =>
                    m.id === tempMessage.id ? { ...m, status: 'sent' } : m
                )
            );
        } catch (error) {
            console.error('Failed to send message:', error);

            // Mark as failed
            setMessages(prev =>
                prev.map(m =>
                    m.id === tempMessage.id ? { ...m, status: 'failed' } : m
                )
            );
        } finally {
            setSendingMessage(false);
        }
    };

    // ===== RETRY FAILED MESSAGE =====
    const handleRetryMessage = async (messageId: string) => {
        const failedMsg = messages.find(m => m.id === messageId && m.status === 'failed');
        if (!failedMsg) return;

        // Remove failed message
        setMessages(prev => prev.filter(m => m.id !== messageId));

        // Resend
        await handleSendMessage(failedMsg.content);
    };

    return (
        <motion.div
            initial={isModal ? { scale: 0.9, opacity: 0, y: 20 } : { opacity: 0 }}
            animate={isModal ? { scale: 1, opacity: 1, y: 0 } : { opacity: 1 }}
            exit={isModal ? { scale: 0.9, opacity: 0, y: 20 } : { opacity: 0 }}
            className={`w-full bg-white dark:bg-surface-900 overflow-hidden pointer-events-auto flex flex-col ${isModal ? 'max-w-4xl h-[600px] rounded-t-2xl lg:rounded-2xl shadow-2xl' : 'h-full'
                }`}
        >
            {/* Header */}
            {isModal && (
                <div className="px-6 py-4 border-b border-surface-200 dark:border-surface-700 flex items-center justify-between bg-gradient-to-r from-mavi-50 to-surface-50 dark:from-mavi-900/20 dark:to-surface-800">
                    <h2 className="font-poppins font-bold text-xl text-gray-900 dark:text-white">
                        Mesajlar
                    </h2>
                    <button
                        onClick={closeChat}
                        className="p-2 hover:bg-surface-200 dark:hover:bg-surface-700 rounded-lg transition-colors"
                        aria-label="Kapat"
                    >
                        <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    </button>
                </div>
            )}

            {/* Main content */}
            <div className="flex-1 flex overflow-hidden relative">
                {/* Left sidebar - Conversation list */}
                <div className={`${isModal ? 'w-full lg:w-80' : 'w-full lg:w-96'} ${activeConversation ? 'hidden lg:flex' : 'flex'} border-r border-surface-200 dark:border-surface-700 flex flex-col bg-surface-50/50 dark:bg-surface-900/50`}>
                    {/* Navigation Header for Full Page view */}
                    {/* New Message Button */}
                    <div className="px-4 py-3 border-b border-surface-200 dark:border-surface-700">
                        <button
                            onClick={() => setShowNewMessage(true)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-br from-mavi-500 to-mavi-600 hover:from-mavi-600 hover:to-mavi-700 text-white rounded-lg font-poppins font-medium text-sm shadow-md hover:shadow-lg transition-all"
                        >
                            <Plus className="w-4 h-4" />
                            Yeni Mesaj
                        </button>
                    </div>

                    <ConversationList conversations={conversations} />
                </div>

                {/* Right panel - Active chat */}
                <div className={`flex-1 ${activeConversation ? 'flex' : 'hidden lg:flex'} flex-col bg-white dark:bg-surface-900`}>
                    {activeConversation && displayConv ? (
                        <>
                            {/* Chat header */}
                            <div className="h-[78px] px-4 lg:px-6 border-b border-surface-200 dark:border-surface-700 flex items-center gap-2 lg:gap-3">
                                {/* Back Button (Mobile only) */}
                                <button
                                    onClick={() => setActiveConversation(null)}
                                    className="lg:hidden p-2 -ml-2 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg transition-colors"
                                >
                                    <ChevronLeft className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                                </button>

                                {displayConv.avatar || displayConv.name ? (
                                    <UserAvatar
                                        src={displayConv.avatar}
                                        name={displayConv.name}
                                        size="md"
                                        isExternal={false}
                                    />
                                ) : (
                                    <div className="w-10 h-10 rounded-lg bg-surface-200 animate-pulse" />
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="font-poppins font-bold text-gray-900 dark:text-white truncate">
                                        {displayConv.name}
                                    </p>
                                    <div className="flex items-center gap-1.5">
                                        {(() => {
                                            const isTyping = typing.get(displayConv.pubkey);
                                            const lastSeen = presence.get(displayConv.pubkey) || 0;
                                            const isOnline = Date.now() / 1000 - lastSeen < 120; // 2 minutes

                                            if (isTyping) {
                                                return (
                                                    <>
                                                        <div className="w-1.5 h-1.5 rounded-full bg-mavi-500 animate-pulse" />
                                                        <span className="text-[10px] text-mavi-500 font-medium">Yazıyor...</span>
                                                    </>
                                                );
                                            }

                                            if (isOnline) {
                                                return (
                                                    <>
                                                        <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                                        <span className="text-[10px] text-green-500 font-medium">Çevrimiçi</span>
                                                    </>
                                                );
                                            }

                                            return (
                                                <>
                                                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                                                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                                                        {lastSeen > 0
                                                            ? `Son görülme: ${formatDistanceToNow(lastSeen * 1000, { addSuffix: true, locale: tr })}`
                                                            : 'Çevrimdışı'}
                                                    </span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => {
                                            if (window.confirm('Bu sohbeti gizlemek istediğinizden emin misiniz?')) {
                                                hideConversation(displayConv.pubkey);
                                                useChatStore.getState().setActiveConversation(null);
                                            }
                                        }}
                                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg transition-all group flex items-center gap-2"
                                        title="Sohbeti Gizle"
                                    >
                                        <span className="text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">Sohbeti Gizle</span>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Messages */}
                            <div className="flex-1 overflow-y-auto p-4 lg:p-6">
                                {loadingMessages || profileLoading ? (
                                    <div className="flex items-center justify-center h-full">
                                        <Loader2 className="w-6 h-6 animate-spin text-mavi-500" />
                                    </div>
                                ) : messages.length > 0 ? (
                                    <>
                                        {messages.map((msg, index) => (
                                            <MessageBubble
                                                key={msg.id}
                                                message={msg}
                                                showEncryptionBadge={index === 0}
                                                onRetry={msg.status === 'failed' ? () => handleRetryMessage(msg.id) : undefined}
                                            />
                                        ))}
                                        {/* Typing Indicator Bubble */}
                                        {activeConversation && typing.get(activeConversation) && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 5 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className="flex justify-start mb-4"
                                            >
                                                <div className="bg-surface-100 dark:bg-surface-800 px-4 py-2 rounded-2xl rounded-bl-md flex gap-1 items-center">
                                                    <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                                                    <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                                                    <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" />
                                                </div>
                                            </motion.div>
                                        )}

                                        {/* Auto-scroll anchor */}
                                        <div ref={messagesEndRef} />
                                    </>
                                ) : (
                                    <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                                        <p className="font-merriweather text-sm">
                                            Henüz mesaj yok. İlk mesajı gönderin!
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Input */}
                            <MessageInput onSend={handleSendMessage} disabled={sendingMessage} />
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="text-center space-y-2">
                                <p className="font-poppins font-semibold text-gray-700 dark:text-gray-300">
                                    Bir sohbet seçin
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Sol taraftan devam etmek istediğiniz sohbeti seçin
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* New Message Modal */}
            {showNewMessage && (
                <NewMessageModal onClose={() => setShowNewMessage(false)} />
            )}
        </motion.div>
    );
}
